import * as React from 'react';

import {
  DropZone as AriaDropZone,
  DropZoneProps as AriaDropZoneProps,
  isFileDropItem,
  composeRenderProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';

export interface DropZoneProps extends Omit<AriaDropZoneProps, 'className'> {
  /**
   * Additional class name for the drop zone component
   */
  className?: string | ((values: { isDropTarget?: boolean }) => string);
}

/**
 * DropZone component for receiving drag and drop content.
 *
 * Provides a designated area where users can drop files or other draggable content.
 * Includes visual feedback when content is dragged over the zone and accessibility features.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <DropZone
 *   onDrop={async (e) => {
 *     const fileItems = e.items.filter(item => item.kind === 'file');
 *     const filePromises = fileItems.map(async (item) => await item.getFile());
 *     const files = await Promise.all(filePromises);
 *     console.log('Dropped files:', files);
 *   }}
 * >
 *   <Text slot="label">Drop files here</Text>
 * </DropZone>
 *
 * // With custom styling
 * <DropZone
 *   className="h-64 w-full border-dashed border-2 border-primary"
 *   onDrop={handleDrop}
 * >
 *   <div>
 *     <p>Drop files to upload</p>
 *     <p className="text-sm text-muted">PNG, JPG, GIF up to 10MB</p>
 *   </div>
 * </DropZone>
 * ```
 */
const DropZone = ({ className, ...props }: DropZoneProps) => (
  <AriaDropZone
    className={composeRenderProps(className, (className) =>
      cn(
        '@DropZone',
        'ring-offset-background flex h-[150px] w-[300px] flex-col items-center justify-center gap-2 rounded-md border border-dashed text-sm',
        /* Drop Target */
        'rac-drop-target:border-solid rac-drop-target:border-primary rac-drop-target:bg-primary',
        /* Focus Visible */
        'rac-focus-visible:ring-2 rac-focus-visible:ring-primary rac-focus-visible:ring-offset-2 rac-focus-visible:outline-none',
        className,
      ),
    )}
    {...props}
  />
);

export { DropZone, isFileDropItem };
